const EMPTY_OBJECT = Object.freeze({});

export const asArray = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item !== undefined && item !== null && item !== "");
  return value === undefined || value === null || value === "" ? [] : [value];
};

export const unique = (values) => Array.from(new Set(asArray(values)));

export function humanizeId(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
}

export function getByPath(source, path) {
  if (!path) return undefined;
  return String(path)
    .split(".")
    .reduce((value, part) => (value && typeof value === "object" ? value[part] : undefined), source);
}

export function hasByPath(source, path) {
  if (!path) return false;
  const parts = String(path).split(".");
  let current = source;
  for (const part of parts) {
    if (!current || typeof current !== "object" || !(part in current)) return false;
    current = current[part];
  }
  return true;
}

export function flattenCategoryTree(categories = []) {
  const flat = [];
  const walk = (items, parentId = null) => {
    for (const category of items || []) {
      if (!category?.id) continue;
      flat.push({ ...category, parentId });
      walk(category.children || [], category.id);
    }
  };
  walk(categories);
  return flat;
}

export function categoryLineage(categoryId, categories = []) {
  if (!categoryId) return [];
  const flat = flattenCategoryTree(categories);
  const byId = new Map(flat.map((category) => [category.id, category]));
  const result = [];
  let current = byId.get(categoryId);
  const visited = new Set();
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    result.unshift(current.id);
    current = current.parentId ? byId.get(current.parentId) : null;
  }
  return result;
}

const appendUnique = (target, values) => {
  for (const value of values || []) {
    if (value && !target.includes(value)) target.push(value);
  }
};

export function resolveCategoryAttributes(categoryId, categories, profileConfig = EMPTY_OBJECT) {
  const displayAttributes = [];
  const filterAttributes = [];

  appendUnique(displayAttributes, profileConfig?.global?.displayAttributes);
  appendUnique(filterAttributes, profileConfig?.global?.filterAttributes);

  const lineage = categoryLineage(categoryId, categories);
  const currentId = categoryId || "";
  for (const assignment of profileConfig?.assignments || []) {
    const assignmentId = String(assignment?.categoryId || "");
    const applies = assignmentId === currentId || (assignment?.includeDescendants && lineage.includes(assignmentId));
    if (!applies) continue;

    for (const profileId of assignment?.profiles || []) {
      const profile = profileConfig?.profiles?.[profileId];
      if (!profile) continue;
      appendUnique(displayAttributes, profile.displayAttributes);
      appendUnique(filterAttributes, profile.filterAttributes);
    }
  }

  return { displayAttributes, filterAttributes };
}

export function labelsAt(schemaLabels = EMPTY_OBJECT, reference) {
  const value = getByPath(schemaLabels, reference);
  return value && typeof value === "object" && !Array.isArray(value) ? value : EMPTY_OBJECT;
}

export function optionLabel(attribute, value, schemaLabels = EMPTY_OBJECT) {
  const labels = labelsAt(schemaLabels, attribute?.labelsRef);
  const key = String(value);
  if (labels[key]) return labels[key];
  if (attribute?.valueType === "number" && attribute?.unit) return `${key} ${attribute.unit}`;
  return humanizeId(key);
}

function unwrapValue(raw, valueType) {
  if (
    raw &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    "value" in raw &&
    !["range", "dimensions"].includes(valueType)
  ) {
    return raw.value;
  }
  return raw;
}

export function attributeValue(product, attribute) {
  if (!attribute) return undefined;
  const raw = getByPath(product, attribute.source);

  if (attribute.valueType === "contains") {
    return asArray(raw).includes(attribute.containsValue);
  }
  if (attribute.valueType === "non-empty") {
    return asArray(raw).length > 0;
  }
  return unwrapValue(raw, attribute.valueType);
}

export function buildProductFacets(product, attributeConfig = EMPTY_OBJECT) {
  const definitions = attributeConfig?.attributes || EMPTY_OBJECT;
  const facets = { ...(product?.facets || {}) };

  for (const [attributeId, attribute] of Object.entries(definitions)) {
    if (!attribute?.filter?.enabled || attributeId in facets) continue;
    const value = attributeValue(product, attribute);
    const isEmptyArray = Array.isArray(value) && value.length === 0;
    if (value === undefined || value === null || value === "" || isEmptyArray) continue;
    facets[attributeId] = value;
  }
  return facets;
}

function numberValue(value) {
  const candidate = value && typeof value === "object" && "value" in value ? value.value : value;
  const parsed = Number(candidate);
  return Number.isFinite(parsed) ? parsed : null;
}

function rangeBounds(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const min = numberValue(value.min ?? value.value);
    const max = numberValue(value.max ?? value.value);
    if (min !== null || max !== null) return { min: min ?? max, max: max ?? min };
  }
  const number = numberValue(value);
  return number === null ? null : { min: number, max: number };
}

function booleanValue(value) {
  if (value === true || value === false) return value;
  if (value === 1 || value === "1") return true;
  if (value === 0 || value === "0") return false;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return null;
}

export function matchesFacetValue(productValue, selectedValue, attribute) {
  const filterType = attribute?.filter?.type;

  if (filterType === "terms" || filterType === "single") {
    const selected = asArray(selectedValue).map(String);
    if (!selected.length) return true;
    const productValues = asArray(productValue).map(String);
    return selected.some((value) => productValues.includes(value));
  }

  if (filterType === "boolean") {
    const selected = booleanValue(selectedValue);
    if (selected === null) return true;
    const productBoolean = booleanValue(productValue);
    return productBoolean !== null && productBoolean === selected;
  }

  if (filterType === "range") {
    if (!selectedValue || typeof selectedValue !== "object") return true;
    const selectedMin = numberValue(selectedValue.min);
    const selectedMax = numberValue(selectedValue.max);
    if (selectedMin === null && selectedMax === null) return true;
    const productBounds = rangeBounds(productValue);
    if (!productBounds) return false;
    if (selectedMin !== null && productBounds.max < selectedMin) return false;
    if (selectedMax !== null && productBounds.min > selectedMax) return false;
    return true;
  }

  return true;
}

export function categoryIdsOf(product) {
  return asArray(product?.categoryIds);
}

export function matchesBaseFilters(product, activeFilters = EMPTY_OBJECT) {
  if (Array.isArray(activeFilters.categoryIds) && activeFilters.categoryIds.length) {
    if (!categoryIdsOf(product).some((id) => activeFilters.categoryIds.includes(id))) return false;
  }

  const functionIds = unique([
    ...(activeFilters.functionId ? [activeFilters.functionId] : []),
    ...asArray(activeFilters.functionIds),
  ]);
  if (functionIds.length) {
    const productFunctions = asArray(product?.functionIds);
    if (!functionIds.some((id) => productFunctions.includes(id))) return false;
  }

  const query = String(activeFilters.q || "").trim().toLocaleLowerCase("hu-HU");
  if (query) {
    const haystack = [
      product?.name,
      product?.subtitle,
      product?.brand,
      product?.seo?.metaTitle,
      product?.seo?.metaDescription,
      ...asArray(product?.tags),
      ...asArray(product?.functionIds),
      ...asArray(product?.apps),
    ]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase("hu-HU");
    if (!haystack.includes(query)) return false;
  }

  return true;
}

export function matchesProductFilters(product, activeFilters, attributeConfig, ignoredFacetId = null, facetsByProductId = null) {
  if (!matchesBaseFilters(product, activeFilters)) return false;
  const facets =
    (product?.id && facetsByProductId?.get(product.id)) ||
    buildProductFacets(product, attributeConfig);
  const definitions = attributeConfig?.attributes || EMPTY_OBJECT;

  for (const [facetId, selectedValue] of Object.entries(activeFilters?.facets || EMPTY_OBJECT)) {
    if (facetId === ignoredFacetId) continue;
    const attribute = definitions[facetId];
    if (!attribute || !matchesFacetValue(facets[facetId], selectedValue, attribute)) return false;
  }
  return true;
}

export function filterProducts(products, activeFilters, attributeConfig) {
  const list = products || [];
  const facetsByProductId = new Map(list.map((product) => [product.id, buildProductFacets(product, attributeConfig)]));
  return list.filter((product) =>
    matchesProductFilters(product, activeFilters, attributeConfig, null, facetsByProductId)
  );
}

function selectedFacetValue(activeFilters, facetId) {
  return activeFilters?.facets?.[facetId];
}

export function facetViewModels({
  products,
  categoryId,
  categories,
  profileConfig,
  attributeConfig,
  schemaLabels,
  activeFilters,
}) {
  const definitions = attributeConfig?.attributes || EMPTY_OBJECT;
  const profile = resolveCategoryAttributes(categoryId, categories, profileConfig);
  const baseProducts = (products || []).filter((product) => matchesBaseFilters(product, activeFilters));
  const facetsByProductId = new Map(
    baseProducts.map((product) => [product.id, buildProductFacets(product, attributeConfig)])
  );
  const result = [];

  for (const facetId of profile.filterAttributes) {
    const attribute = definitions[facetId];
    if (!attribute?.filter?.enabled) continue;
    const selected = selectedFacetValue(activeFilters, facetId);
    const candidates = baseProducts.filter((product) =>
      matchesProductFilters(product, activeFilters, attributeConfig, facetId, facetsByProductId)
    );
    const values = candidates.map((product) => facetsByProductId.get(product.id)?.[facetId]);
    const type = attribute.filter.type;

    if (type === "terms" || type === "single") {
      const counts = new Map();
      for (const productValue of values) {
        for (const value of unique(asArray(productValue).map(String))) {
          counts.set(value, (counts.get(value) || 0) + 1);
        }
      }
      const selectedValues = asArray(selected).map(String);
      for (const value of selectedValues) {
        if (!counts.has(value)) counts.set(value, 0);
      }
      const options = Array.from(counts.entries())
        .map(([value, count]) => ({ value, count, label: optionLabel(attribute, value, schemaLabels) }))
        .sort((a, b) => a.label.localeCompare(b.label, "hu", { sensitivity: "base" }));
      if (!options.length) continue;
      result.push({ id: facetId, attribute, type, options, selected: selectedValues });
      continue;
    }

    if (type === "boolean") {
      const booleanValues = values.map(booleanValue).filter((value) => value !== null);
      const trueCount = booleanValues.filter((value) => value === true).length;
      const falseCount = booleanValues.filter((value) => value === false).length;
      if (!booleanValues.length && selected === undefined) continue;
      result.push({
        id: facetId,
        attribute,
        type,
        trueCount,
        falseCount,
        totalCount: booleanValues.length,
        selected,
      });
      continue;
    }

    if (type === "range") {
      const bounds = values.map(rangeBounds).filter(Boolean);
      if (!bounds.length) {
        if (selected) {
          const selectedMin = numberValue(selected.min) ?? 0;
          const selectedMax = numberValue(selected.max) ?? selectedMin;
          result.push({ id: facetId, attribute, type, min: selectedMin, max: selectedMax, selected });
        }
        continue;
      }
      const min = Math.min(...bounds.map((item) => item.min));
      const max = Math.max(...bounds.map((item) => item.max));
      result.push({ id: facetId, attribute, type, min, max, selected: selected || {} });
    }
  }

  return result;
}

export function groupFacetViews(facets, attributeConfig = EMPTY_OBJECT) {
  const groups = attributeConfig?.filterGroups || EMPTY_OBJECT;
  const byGroup = new Map();
  for (const facet of facets || []) {
    const groupId = facet.attribute?.filter?.group || "product";
    if (!byGroup.has(groupId)) {
      byGroup.set(groupId, {
        id: groupId,
        label: groups[groupId]?.label || humanizeId(groupId),
        order: Number(groups[groupId]?.order ?? 1000),
        defaultOpen: groups[groupId]?.defaultOpen !== false,
        facets: [],
      });
    }
    byGroup.get(groupId).facets.push(facet);
  }
  for (const group of byGroup.values()) {
    group.facets.sort((a, b) => Number(a.attribute?.filter?.order ?? 1000) - Number(b.attribute?.filter?.order ?? 1000));
  }
  return Array.from(byGroup.values()).sort((a, b) => a.order - b.order);
}

const huNumber = new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 2 });

function formatNumber(value, attribute) {
  const number = numberValue(value);
  if (number === null) return "";
  const decimals = attribute?.decimals;
  const formatter = Number.isInteger(decimals)
    ? new Intl.NumberFormat("hu-HU", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : huNumber;
  return formatter.format(number);
}

function noteOf(value) {
  return value && typeof value === "object" && typeof value.note === "string" ? value.note.trim() : "";
}

export function formatAttributeValue(value, attribute, schemaLabels = EMPTY_OBJECT) {
  if (value === undefined || value === null || value === "") return "";
  const type = attribute?.valueType;
  const unit = attribute?.unit ? ` ${attribute.unit}` : "";
  const note = noteOf(value);
  let formatted = "";

  if (type === "boolean" || type === "contains" || type === "non-empty") {
    formatted = value ? attribute?.trueLabel || "Igen" : attribute?.falseLabel || "Nem";
  } else if (type === "enum") {
    formatted = optionLabel(attribute, unwrapValue(value, type), schemaLabels);
  } else if (type === "list-enum") {
    formatted = asArray(value).map((item) => optionLabel(attribute, item, schemaLabels)).join(", ");
  } else if (type === "number") {
    formatted = `${formatNumber(value, attribute)}${unit}`;
  } else if (type === "range") {
    const bounds = rangeBounds(value);
    if (!bounds) return "";
    formatted = bounds.min === bounds.max
      ? `${huNumber.format(bounds.min)}${unit}`
      : `${huNumber.format(bounds.min)}–${huNumber.format(bounds.max)}${unit}`;
  } else if (type === "dimensions" && value && typeof value === "object") {
    const order = ["width", "depth", "height", "diameter", "length"];
    const parts = order
      .filter((key) => numberValue(value[key]) !== null)
      .map((key) => huNumber.format(numberValue(value[key])));
    formatted = parts.length ? `${parts.join(" × ")}${unit}` : "";
  } else if (Array.isArray(value)) {
    formatted = value.map(String).join(", ");
  } else if (typeof value === "object") {
    formatted = Object.entries(value)
      .filter(([key]) => key !== "note")
      .map(([key, item]) => `${humanizeId(key)}: ${String(item)}`)
      .join(", ");
  } else {
    formatted = String(value);
  }

  return note && formatted ? `${formatted}, ${note}` : formatted;
}

export function buildSpecificationRows(product, categoryId, categories, profileConfig, attributeConfig, schemaLabels) {
  const definitions = attributeConfig?.attributes || EMPTY_OBJECT;
  const { displayAttributes } = resolveCategoryAttributes(categoryId, categories, profileConfig);
  const sections = attributeConfig?.sections || EMPTY_OBJECT;
  const rows = [];
  const handledSpecKeys = new Set();

  for (const attributeId of displayAttributes) {
    const attribute = definitions[attributeId];
    if (!attribute?.display?.enabled || !hasByPath(product, attribute.source)) continue;
    const value = attributeValue(product, attribute);
    const formattedValue = formatAttributeValue(value, attribute, schemaLabels);
    if (!formattedValue) continue;
    if (attribute.source?.startsWith("specs.")) handledSpecKeys.add(attribute.source.slice(6));
    rows.push({
      id: attributeId,
      label: attribute.label || humanizeId(attributeId),
      value: formattedValue,
      sectionId: attribute.display.section || "general",
      sectionLabel: sections[attribute.display.section]?.label || humanizeId(attribute.display.section || "general"),
      sectionOrder: Number(sections[attribute.display.section]?.order ?? 1000),
      order: Number(attribute.display.order ?? 1000),
    });
  }

  for (const [key, value] of Object.entries(product?.specs || EMPTY_OBJECT)) {
    if (handledSpecKeys.has(key) || value === undefined || value === null || value === "") continue;
    const formatted = typeof value === "object" ? JSON.stringify(value, null, 0) : String(value);
    rows.push({
      id: `custom:${key}`,
      label: humanizeId(key),
      value: formatted,
      sectionId: "more",
      sectionLabel: "További adatok",
      sectionOrder: 9999,
      order: 9999,
    });
  }

  return rows.sort((a, b) => a.sectionOrder - b.sectionOrder || a.order - b.order || a.label.localeCompare(b.label, "hu"));
}

export function activeFacetCount(activeFilters) {
  return Object.values(activeFilters?.facets || EMPTY_OBJECT).filter((value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === "object") return value.min !== "" || value.max !== "";
    return value !== undefined && value !== null && value !== "";
  }).length;
}

export function facetsFromSearchParams(searchParams, attributeConfig = EMPTY_OBJECT) {
  const definitions = attributeConfig?.attributes || EMPTY_OBJECT;
  const facets = {};
  for (const [key, raw] of searchParams.entries()) {
    if (!key.startsWith("f.")) continue;
    const facetId = key.slice(2);
    const attribute = definitions[facetId];
    if (!attribute) continue;
    const type = attribute.filter?.type;
    if (type === "terms" || type === "single") facets[facetId] = raw.split(",").filter(Boolean);
    else if (type === "boolean") {
      const parsed = booleanValue(raw);
      if (parsed !== null) facets[facetId] = parsed;
    } else if (type === "range") {
      const [min = "", max = ""] = raw.split("..");
      facets[facetId] = { min, max };
    }
  }
  return facets;
}

export function writeFacetsToSearchParams(searchParams, facets) {
  const next = new URLSearchParams(searchParams);
  for (const key of Array.from(next.keys())) {
    if (key.startsWith("f.")) next.delete(key);
  }
  for (const [facetId, value] of Object.entries(facets || EMPTY_OBJECT)) {
    if (Array.isArray(value) && value.length) next.set(`f.${facetId}`, value.join(","));
    else if (typeof value === "boolean") next.set(`f.${facetId}`, value ? "1" : "0");
    else if (value && typeof value === "object" && (value.min !== "" || value.max !== "")) {
      next.set(`f.${facetId}`, `${value.min ?? ""}..${value.max ?? ""}`);
    }
  }
  return next;
}
